import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';

import { useAuth } from '@/lib/AuthContext';
import { isOwner, can } from '@/lib/permissions';
import { getGbusinessMe } from '@/api/gbusinessService';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/components/LanguageContext';
import { apiMessage } from '@/api/apiMessage';
import { buildRegisterUrl, readRedirectParam, sanitizeRedirect } from '@/lib/authRedirect';
import { TRAS_LOGIN_STATE } from '@/components/ofertaPt/anuncios/useAbrirTrasLogin';
import httpClient from '@/api/httpClient';
import PageSpinner from '@/components/PageSpinner';
import './landingV2.css';

const LOGO_SRC = '/landing-images/logo.png';
const INPUT_CLS =
  'mt-1.5 w-full h-11 px-3.5 rounded-xl border border-line bg-mist text-sm text-ink focus:outline-none focus:border-[#feae2c] focus:bg-white transition-colors';

// Tope para resolver el destino por defecto. El endpoint es barato (dos
// lecturas de Mongo), pero el login es el camino más crítico de la app y el
// timeout del httpClient son 15 s: si algo va lento, se entra por la puerta de
// siempre en vez de dejar al usuario mirando el spinner.
const LANDING_RESOLVE_TIMEOUT_MS = 2500;

/**
 * Destino por defecto (cuando no hay `?redirect=`).
 *
 * Quien ya tiene Google conectado Y al menos un local activo entra directo a
 * Mi Empresa, que es su pantalla de trabajo. El resto sigue aterrizando en
 * Dispositivos (`/`), que es donde están el banner de "conecta tu cuenta" y el
 * flujo de alta — mandarlos a Mi Empresa sería enseñarles la versión vacía.
 *
 * Se resuelve con `fetchQuery` sobre la MISMA clave y `staleTime` que usa el
 * Layout (`['gbusiness','me']`), así que no es una petición extra: es la que el
 * Layout iba a lanzar igualmente, adelantada, y el resultado le queda cacheado.
 * Ante cualquier duda —error, lentitud, empleado sin permiso— se cae a '/'.
 */
async function resolveDefaultLanding(queryClient, user) {
  // Un empleado sin `view_my_business` acabaría en RestrictedPage (ver
  // MyBusiness.jsx): para él la puerta de siempre es la correcta.
  if (!isOwner(user) && !can(user, 'view_my_business')) return '/';
  try {
    const me = await Promise.race([
      queryClient.fetchQuery({
        queryKey: ['gbusiness', 'me'],
        queryFn: getGbusinessMe,
        staleTime: 5 * 60 * 1000,
        retry: false,
      }),
      new Promise((resolve) => { setTimeout(() => resolve(null), LANDING_RESOLVE_TIMEOUT_MS); }),
    ]);
    // Plano Google Maps pt: con la oferta activa entra siempre a Dispositivos,
    // que es donde está su tarjeta y donde se le abre la ventana del plan
    // (`useAbrirTrasLogin`).
    if (me?.ofertaPt?.activa) return '/';
    const hasActiveLocation = (me?.activeLocationIds || []).length > 0;
    return me?.connected && hasActiveLocation ? '/MyBusiness' : '/';
  } catch {
    return '/';
  }
}

/**
 * Resuelve a dónde navegar después del login/registro.
 * Si el redirect viene de un escaneo QR (/redirect/?id=...), va directamente
 * a la página de configuración del producto según su tipo.
 * En cualquier otro caso devuelve la URL de redirect original o, si no hay
 * ninguna, el destino por defecto según el estado de la cuenta.
 *
 * El destino se vuelve a sanear aquí aunque quien llama ya lo haya hecho: es
 * la última función antes del `navigate()`, y ningún destino debe llegar a la
 * navegación sin haber pasado por la validación (ver @/lib/authRedirect).
 */
async function resolvePostAuthRedirect(rawRedirect, queryClient, user) {
  const redirectUrl = sanitizeRedirect(rawRedirect);
  if (!redirectUrl) return resolveDefaultLanding(queryClient, user);
  try {
    const url = new URL(redirectUrl, window.location.origin);
    const qrId = url.searchParams.get('id');
    if (url.pathname.includes('/redirect') && qrId) {
      const { data } = await httpClient.get(`/qr/check/${qrId}/`);
      const platform = data?.product?.platform || '';
      if (platform === 'todo_en_1' || platform === 'tipo_todo_en_1') {
        return `/LinkTreeConfig?qrId=${qrId}`;
      }
      return `/RegisterQR?qrId=${qrId}`;
    }
  } catch {
    // Si falla la llamada al API, fallback al redirect original
  }
  return redirectUrl;
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [token2FA, setToken2FA] = useState('');
  const { login, verify2FA, isAuthenticated, isLoadingAuth, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useLanguage();
  // El submit en curso navega él mismo (con la marca del login en el `state`).
  // `login()` pone `isAuthenticated` a true antes de volver, así que sin esto
  // el efecto de abajo también navegaba: los dos esperan la misma petición
  // `me`, el suyo llegaba el último y su `replace` sin `state` borraba la marca.
  const enviando = useRef(false);

  // Destino al que volver tras entrar, ya validado (ver @/lib/authRedirect).
  // Acepta `redirect` —el canónico, el que usan el escaneo QR y el checkout— y
  // `next` como alias, para poder mandar enlaces legibles por correo. Es null
  // si no viene ninguno o si el que viene no es una ruta interna nuestra.
  const redirectTarget = readRedirectParam(searchParams);
  const registerHref = buildRegisterUrl(redirectTarget);

  // ── Redirect already-authenticated users away from /login ──
  // Usa el MISMO resolvedor que el submit. Si mandara a '/' a secas, esta rama
  // se dispararía al marcarse `isAuthenticated` durante el propio login y se
  // vería un parpadeo de Dispositivos antes del destino real.
  useEffect(() => {
    if (isLoadingAuth || !isAuthenticated || enviando.current) return undefined;
    let cancelled = false;
    (async () => {
      const target = await resolvePostAuthRedirect(redirectTarget, queryClient, user);
      if (!cancelled) navigate(target, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [isLoadingAuth, isAuthenticated, navigate, redirectTarget, queryClient, user]);

  // Show spinner while checking session (prevents "flash of login form").
  if (isLoadingAuth || isAuthenticated) {
    return <PageSpinner />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    enviando.current = true;
    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await login(email, password, rememberMe);
      if (result.success) {
        if (result.requires_2fa) {
          setShow2FA(true);
          toast({
            title: 'Autenticación en dos pasos',
            description: 'Por favor ingresa tu código de la aplicación autenticadora.',
          });
          return;
        }

        if (result.requires_2fa_setup) {
          toast({
            title: 'Configuración 2FA requerida',
            description: 'Como administrador, es obligatorio configurar la autenticación en dos pasos.',
            variant: 'destructive',
          });
          navigate('/settings/other');
          return;
        }

        toast({ title: t('login_success_title'), description: t('login_success_desc') });
        const target = await resolvePostAuthRedirect(redirectTarget, queryClient, result.user);
        // La marca del login normal: Dispositivos abre con ella la ventana del
        // Plano pt si le toca (`useAbrirTrasLogin`). Al resto no le hace nada.
        navigate(target, { state: { [TRAS_LOGIN_STATE]: Date.now() } });
      } else {
        setErrorMessage(apiMessage(result, t, t('login_error_invalid')));
      }
    } catch {
      enviando.current = false;
      setErrorMessage(t('login_error_unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    enviando.current = true;
    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await verify2FA(token2FA);
      if (result.success) {
        toast({ title: t('login_success_title'), description: t('login_success_desc') });
        const target = await resolvePostAuthRedirect(redirectTarget, queryClient, result.user);
        navigate(target, { state: { [TRAS_LOGIN_STATE]: Date.now() } });
      } else {
        setErrorMessage(apiMessage(result, t, t('api.errors.invalid_token')));
      }
    } catch {
      enviando.current = false;
      setErrorMessage('Error inesperado al verificar 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="landing-v2 min-h-screen w-full overflow-x-hidden flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
            <div className="text-center">
              <Link to="/" className="inline-flex"><img src={LOGO_SRC} alt="Tapstar" className="h-8 w-auto mx-auto" /></Link>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] mt-6">{t('welcome_title')}</h1>
              <p className="mt-2 text-slate-500">{t('login_subtitle')}</p>
            </div>

            {!show2FA ? (
              <form onSubmit={handleSubmit} className="mt-8 bg-white rounded-2xl border border-line soft-shadow-lg p-6 sm:p-7 space-y-4">
                <label className="block">
                  <span className="text-[13px] font-semibold text-ink">{t('login_identifier_label')}</span>
                  <input
                    type="text"
                    name="email"
                    autoComplete="username"
                    placeholder={t('login_identifier_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={INPUT_CLS}
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-[13px] font-semibold text-ink">{t('password_label')}</span>
                  <div className="relative">
                    {/* id/name/autocomplete fijos: el maskInputFn de PostHog
                        (main.jsx) detecta la contraseña por estos atributos,
                        que NO cambian al togglear "mostrar" (type pasa a 'text').
                        Sin ellos el replay mostraba la contraseña en claro. */}
                    <input
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${INPUT_CLS} pr-11`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ink transition-colors"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </label>

                <div className="flex items-center justify-between -mt-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-line accent-[#feae2c] cursor-pointer"
                    />
                    <span className="text-[13px] text-slate-500">{t('remember_me')}</span>
                  </label>
                  <Link to="/password-reset" className="text-[13px] font-medium text-amberdeep hover:underline">{t('forgot_password')}</Link>
                </div>

                {errorMessage && (
                  <div className="bg-rose-50 text-rose-600 p-3 rounded-xl text-sm font-medium border border-rose-100 flex items-start gap-2">
                    <span className="font-bold">{t('error_label')}:</span> {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center gap-1.5 w-full h-12 rounded-full font-semibold text-black bg-[#feae2c] hover:bg-[#e89d1a] transition-colors disabled:opacity-70"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t('login_button')} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            ) : (
              <form onSubmit={handle2FASubmit} className="mt-8 bg-white rounded-2xl border border-line soft-shadow-lg p-6 sm:p-7 space-y-5">
                <div className="text-center space-y-2">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-mist mb-1"><ShieldCheck className="w-8 h-8 text-[#feae2c]" /></div>
                  <h3 className="text-xl font-bold text-ink">Verificación de seguridad</h3>
                  <p className="text-sm text-slate-500">Ingresa el código de 6 dígitos generado por tu aplicación autenticadora.</p>
                </div>

                <label className="block">
                  <span className="text-[13px] font-semibold text-ink">Código de verificación</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={token2FA}
                    onChange={(e) => setToken2FA(e.target.value)}
                    className={`${INPUT_CLS} h-14 text-center text-2xl tracking-widest font-mono`}
                    required
                    autoFocus
                  />
                </label>

                {errorMessage && (
                  <div className="bg-rose-50 text-rose-600 p-3 rounded-xl text-sm font-medium border border-rose-100 flex items-start gap-2">
                    <span className="font-bold">{t('error_label')}:</span> {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || token2FA.length < 6}
                  className="inline-flex items-center justify-center gap-1.5 w-full h-12 rounded-full font-semibold text-black bg-[#feae2c] hover:bg-[#e89d1a] transition-colors disabled:opacity-60"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar código'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShow2FA(false); setToken2FA(''); setErrorMessage(''); }}
                  className="w-full h-11 rounded-full text-sm font-semibold text-slate-500 hover:text-ink transition-colors"
                >
                  Cancelar y volver
                </button>
              </form>
            )}

            <p className="mt-5 text-center text-[14px] text-slate-500">
              {t('no_account')} <Link to={registerHref} className="font-semibold text-amberdeep hover:underline">{t('register_now')}</Link>
            </p>
      </div>
    </div>
  );
};

export default Login;
